// On-brand presets — the loud combos sit first because they're the ones we
// want users to actually pick. The picker also accepts arbitrary colors via
// the native <input type="color"> control, with hex entry tucked behind an
// "Advanced" disclosure (most users don't think in hex).
const PRESETS = [
  { label: "Voltage on coal",  foreground: "#22C55E", background: "#111111" },
  { label: "Coal on cloud",    foreground: "#111111", background: "#FAFAFA" },
  { label: "Plasma on cloud",  foreground: "#7C3AED", background: "#FAFAFA" },
  { label: "Coal on voltage",  foreground: "#111111", background: "#22C55E" }
]

// Same Rec. 709 weights as the server-side validation in QrCode#contrast_within_budget.
// GradientPicker reads it too.
export function relativeLuminance(hex) {
  const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex || "")
  if (!m) return null
  let s = m[1]
  if (s.length === 3) s = s.split("").map((c) => c + c).join("")
  const r = parseInt(s.slice(0, 2), 16) / 255
  const g = parseInt(s.slice(2, 4), 16) / 255
  const b = parseInt(s.slice(4, 6), 16) / 255
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function contrastOK(fg, bg) {
  const lf = relativeLuminance(fg)
  const lb = relativeLuminance(bg)
  if (lf == null || lb == null) return null
  return Math.abs(lb - lf) >= 0.4
}

function normalizeHex(value) {
  if (!value) return ""
  let s = String(value).trim()
  if (!s) return ""
  if (!s.startsWith("#")) s = "#" + s
  if (s.length === 4) {
    const [_, r, g, b] = s
    s = `#${r}${r}${g}${g}${b}${b}`
  }
  return s.toUpperCase()
}

// The arbitrary-selector classes strip the native chrome the browser draws
// around the color input (the outer rounded "well" + inset swatch with a gap)
// so the whole tile reads as a single color chip, matching the preset combo
// swatches above. GradientPicker's end-color chip uses it too.
export const SWATCH_INPUT_CLASS = [
  "block w-10 h-10 rounded border border-[color:var(--color-border)] dark:border-storm cursor-pointer p-0 bg-transparent appearance-none",
  "[&::-webkit-color-swatch-wrapper]:p-0",
  "[&::-webkit-color-swatch]:border-none",
  "[&::-webkit-color-swatch]:rounded",
  "[&::-moz-color-swatch]:border-none",
  "[&::-moz-color-swatch]:rounded"
].join(" ")

function Swatch({ label, value, fallback, onChange }) {
  return (
    <label className="flex items-center gap-3 text-sm">
      <input
        type="color"
        value={value || fallback}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        aria-label={`${label} color`}
        className={SWATCH_INPUT_CLASS}
      />
      <span>{label}</span>
    </label>
  )
}

export default function ColorPicker({ foreground, background, onChange }) {
  const ok = contrastOK(foreground, background)
  const activeFg = normalizeHex(foreground)
  const activeBg = normalizeHex(background)

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {PRESETS.map((p) => {
          const selected = activeFg === p.foreground.toUpperCase() && activeBg === p.background.toUpperCase()
          return (
            <button
              key={p.label}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange({ foreground: p.foreground, background: p.background })}
              className={
                "flex items-center gap-2 px-3 py-2 rounded border transition text-sm text-left " +
                (selected
                  ? "border-voltage bg-voltage/10 ring-2 ring-voltage/40"
                  : "border-[color:var(--color-border)] dark:border-storm hover:border-voltage")
              }
            >
              <span
                aria-hidden="true"
                className="inline-flex w-6 h-6 rounded items-center justify-center border border-[color:var(--color-border)] dark:border-storm"
                style={{ background: p.background, color: p.foreground }}
              >
                <span className="block w-3 h-3 rounded-sm" style={{ background: p.foreground }} />
              </span>
              {p.label}
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Swatch
          label="Foreground"
          value={foreground}
          fallback="#111111"
          onChange={(hex) => onChange({ foreground: hex, background })}
        />
        <Swatch
          label="Background"
          value={background}
          fallback="#FFFFFF"
          onChange={(hex) => onChange({ foreground, background: hex })}
        />
      </div>

      <details>
        <summary className="cursor-pointer select-none text-xs text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud">
          Advanced — enter hex codes
        </summary>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="block text-[color:var(--color-muted)] dark:text-muted-light mb-1">Foreground hex</span>
            <input
              type="text"
              value={foreground || ""}
              onChange={(e) => onChange({ foreground: e.target.value, background })}
              placeholder="#111111"
              className="w-full px-2 py-1 rounded border border-[color:var(--color-border)] dark:border-storm font-mono text-sm focus:outline-none focus:ring-2 focus:ring-voltage focus:border-voltage"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-[color:var(--color-muted)] dark:text-muted-light mb-1">Background hex</span>
            <input
              type="text"
              value={background || ""}
              onChange={(e) => onChange({ foreground, background: e.target.value })}
              placeholder="#FFFFFF"
              className="w-full px-2 py-1 rounded border border-[color:var(--color-border)] dark:border-storm font-mono text-sm focus:outline-none focus:ring-2 focus:ring-voltage focus:border-voltage"
            />
          </label>
        </div>
      </details>

      {/* Live scan-contrast feedback: a positive confirmation when the pair is
          fine, an amber warning (not a harsh red — low contrast is a caution,
          not an error) when it's too low. Both are colored banners. */}
      <div aria-live="polite">
        {ok === false && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded text-sm bg-warn/10 dark:bg-warn/15 text-coal dark:text-cloud">
            {/* Amber (warn) icon carries the "caution" colour; the message stays
                coal so it's legible — amber-500 as body text on the pale tint
                fails contrast. */}
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5 text-warn" aria-hidden="true">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>Contrast is too low. Phones may struggle to scan this code. Pick a darker or lighter pair.</span>
          </div>
        )}
        {ok === true && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded text-sm bg-voltage-pale dark:bg-voltage/15 text-voltage-dark dark:text-voltage-light">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Good contrast. This should scan cleanly.</span>
          </div>
        )}
      </div>
    </div>
  )
}
